import { trainDateForSide } from "../EndToEndJourney/shuttleOffersController";
import { dubaiDate } from "../../Lib/Utils/shuttleTime";
import { formatDayAndMonthByLocale } from "../../Lib/Utils/validationUtil";
import { ILegTrain } from "../../Orchestrator/Types/Index";
import { RideHailingMode } from "../../Orchestrator/Types/Interface/IRideHailing";
import { IShuttleAddedJourney } from "../../Orchestrator/Types/Interface/IShuttleBus";

/**
 * Does this booked shuttle run on the same day as the train leg it belongs to?
 *
 * A change-date exchanges the TRAIN offers only (`exchangeOffers` in
 * StartPreExchangeOperationAction) — the shuttle is a separate booked offer and
 * is left behind on the original date. Every other field on the entry (stop,
 * time, price) looks perfectly current, so without this comparison the stale
 * shuttle renders as if it still connects to the train.
 *
 * Returns "unknown" when either date is unparseable: an unknown date must never
 * be reported as a mismatch, or a display quirk turns into a false "your shuttle
 * is on the wrong day" and pushes the customer into a needless refund.
 *
 * SIDE-AWARE, for the same reason shuttleDepartureDate is: the shuttle is
 * booked for the day it MEETS the train, which on an overnight leg is not the
 * same calendar day at both ends. Abu Dhabi -> Fujairah departs 23:31 and
 * arrives 00:37 the next day, so a correct arrival-side shuttle on the 20th was
 * compared against a departure on the 19th and reported as a mismatch — telling
 * the customer to refund a shuttle that was exactly right.
 */
export type ShuttleDateMatch = "match" | "mismatch" | "unknown";

export const shuttleDateMatch = (
  /** Structural, not `IShuttleAddedJourney`: the pre-collapse callers in
      mapBookedShuttlesToSides hold a NormalizedBookedShuttle, and only
      `depDateTime` is ever read. */
  entry: { depDateTime?: string | null } | null | undefined,
  trainLeg: ILegTrain | null | undefined,
  side: RideHailingMode
): ShuttleDateMatch => {
  const shuttleDay = dubaiDate(entry?.depDateTime);
  const trainDay = dubaiDate(trainDateForSide(side, trainLeg ?? undefined));
  if (!shuttleDay || !trainDay) return "unknown";
  return shuttleDay === trainDay ? "match" : "mismatch";
};

/**
 * "Tue, 28 Jul " for a card's time line, or "" when the date is unusable.
 *
 * The trailing space is deliberate: callers concatenate it straight onto the
 * departure time, and an empty label must not leave a stray gap. Guarding on the
 * PARSED date rather than the raw field is what stops `formatDayAndMonthByLocale`
 * printing the literal string "Invalid Date" beside the times.
 */
export const shuttleDateLabel = (depDateTime?: string | null): string => {
  const day = dubaiDate(depDateTime);
  return day ? `${formatDayAndMonthByLocale(day)} ` : "";
};
